/**
 * Workflow Versions v2 — state + data types.
 *
 * Types are derived from a live backend audit (Commit 2 audit step,
 * see commit message). Every field below was confirmed against the
 * backend Pydantic / route handlers, NOT inferred from the mockup's
 * mockData.js. Mock-only fields that the backend doesn't ship are
 * NOT modelled here — see "Out of scope" in the plan.
 *
 * Live sources:
 *   - List route       : backend/src/api/v1/version_routes.py:44-63
 *                          → backend/src/services/version_service.py:451-482
 *   - Detail route     : backend/src/api/v1/version_routes.py:66-84
 *                          → service _insert_version (lines 279-302)
 *   - Restore route    : backend/src/api/v1/version_routes.py:87-111
 *                          → service line 796-804
 */

// ────────────────────────────────────────────────────────────────
// Operation type — exhaustive enum matching the backend's
// VersionRecord.operation_type values. Verified by grep against
// backend/src/: only these seven strings ever appear as
// operation_type. (`bulk_import` was a mock-only operation; the
// backend has no path that creates one, so it's intentionally
// absent here.)
// ────────────────────────────────────────────────────────────────
export type OperationType =
  | 'baseline'
  | 'snapshot'
  | 'add'
  | 'update'
  | 'delete'
  | 'restore'
  | 'revise_recompletion';

// ────────────────────────────────────────────────────────────────
// Per-operation diff payloads. Each diff carries a `kind`
// discriminant matching its operation_type so consumers can
// type-narrow at the use site.
// ────────────────────────────────────────────────────────────────

/** Single field change inside an `update` diff. */
export interface FieldChange {
  field: string;
  old_value: unknown;
  new_value: unknown;
}

export interface UpdateDiff {
  kind: 'update';
  agent_name: string;
  array_key: string;
  id_field: string;
  record_id: string;
  field_changes: FieldChange[];
}

export interface AddDiff {
  kind: 'add';
  agent_name: string;
  array_key: string;
  id_field: string;
  record_id: string;
  added_record: Record<string, unknown>;
}

/** Each cascaded delete carries the FULL record body — the audit
 *  showed this is `deleted_record`, not `record` as the mockup's
 *  `// EXTRA` comment implied. */
export interface CascadeDelete {
  agent_name: string;
  array_key: string;
  id_field: string;
  record_id: string;
  deleted_record: Record<string, unknown>;
}

export interface DeleteDiff {
  kind: 'delete';
  agent_name: string;
  array_key: string;
  id_field: string;
  record_id: string;
  deleted_record: Record<string, unknown>;
  cascade_deleted: CascadeDelete[];
}

export interface RestoreDiff {
  kind: 'restore';
  restore_target_version: number;
  reconstructed_from: {
    snapshot_version: number;
    diffs_applied: number;
  };
  /** Sorted list of agent names touched by the restore. */
  agents_affected: string[];
}

export interface PerAgentReviseSummary {
  agent_name: string;
  added: number;
  updated: number;
  removed: number;
}

export interface ReviseRecompletionDiff {
  kind: 'revise_recompletion';
  target_agent: string | null;
  agents_changed: string[];
  per_agent_summary: PerAgentReviseSummary[];
  baseline_version_before_revise: number;
}

/** baseline + snapshot diffs are `null` on the backend — the
 *  snapshot itself carries the state, and there's no operation
 *  diff to render. We model the diff as `null` rather than as an
 *  empty object so consumers branch on type-narrowing. */
export type OperationDiff =
  | UpdateDiff
  | AddDiff
  | DeleteDiff
  | RestoreDiff
  | ReviseRecompletionDiff
  | null;

// ────────────────────────────────────────────────────────────────
// VersionRecord — one entry in the timeline. Mirrors the Mongo
// document inserted by VersionService._insert_version (lines
// 279-302).
// ────────────────────────────────────────────────────────────────
export interface VersionRecord {
  version_id: string;
  thread_id: string;
  tenant_id: string | null;
  session_id: string | null;
  version_number: number;
  parent_version_number: number | null;
  is_snapshot: boolean;
  /** Heavy payload, omitted by default. Populated only when the
   *  detail endpoint is called with `include_snapshot=true`. */
  snapshot?: { agent_progress: Record<string, unknown> } | null;
  operation_type: OperationType;
  agent_name: string | null;
  array_key: string | null;
  id_field: string | null;
  record_ids: string[];
  diff: OperationDiff;
  summary: string;
  user_id: string | null;
  user_email: string | null;
  user_display_name: string;
  /** ISO datetime string. */
  created_at: string;
  source_endpoint: string | null;
  restore_target_version: number | null;
  cumulative_change_count: number | null;
}

// ────────────────────────────────────────────────────────────────
// List endpoint response wrapper. The legacy VersionsPanel pulls
// these four fields off the response (`items`, `total`,
// `current_version_number`) — see VersionsPanel.jsx:75-77.
// ────────────────────────────────────────────────────────────────
export interface VersionListResponse {
  items: VersionRecord[];
  total: number;
  offset: number;
  limit: number;
  current_version_number: number;
}

// ────────────────────────────────────────────────────────────────
// Restore endpoint response. Quoted directly from
// version_service.py:796-804.
// ────────────────────────────────────────────────────────────────
export interface RestoreResponse {
  status: 'success';
  restored_from: number;
  new_version_number: number;
  summary: string;
  snapshot_version: number;
  diffs_applied: number;
  /** Sorted list of agent names touched. */
  agents_affected: string[];
}

// ────────────────────────────────────────────────────────────────
// Reducer state — one slice per concern.
// ────────────────────────────────────────────────────────────────
export interface VersionsState {
  /** Versions in fetched order (newest first). Same as the
   *  backend's `items` order; cumulative across pagination
   *  load-more clicks. */
  versions: VersionRecord[];
  /** Random-access index by version_number; kept in sync with
   *  `versions` so card lookups + restore-target resolution are
   *  O(1). */
  byNumber: Record<number, VersionRecord>;
  /** Set of version_numbers whose cards are currently expanded. */
  expanded: Set<number>;
  /** Set of version_numbers whose detail fetch is in flight. */
  diffLoadingByNumber: Set<number>;
  /** version_number the user has flagged for restore confirmation,
   *  or null when no dialog is open. */
  pendingRestore: number | null;
  restoring: boolean;
  /** Initial list fetch state. */
  loading: boolean;
  error: string | null;
  /** Pagination cursor + totals. */
  pagination: {
    offset: number;
    limit: number;
    total: number;
    currentVersionNumber: number;
  };
}

export const initialVersionsState: VersionsState = {
  versions: [],
  byNumber: {},
  expanded: new Set(),
  diffLoadingByNumber: new Set(),
  pendingRestore: null,
  restoring: false,
  loading: false,
  error: null,
  pagination: { offset: 0, limit: 20, total: 0, currentVersionNumber: 0 },
};

// ────────────────────────────────────────────────────────────────
// Action union for the reducer.
// ────────────────────────────────────────────────────────────────
export type VersionsAction =
  | { type: 'FETCH_START' }
  | {
      type: 'FETCH_SUCCESS';
      payload: { response: VersionListResponse; append: boolean };
    }
  | { type: 'FETCH_ERROR'; payload: string }
  | { type: 'TOGGLE_EXPAND'; payload: number }
  | { type: 'DIFF_FETCH_START'; payload: number }
  | {
      type: 'DIFF_FETCH_SUCCESS';
      payload: { versionNumber: number; record: VersionRecord };
    }
  | { type: 'DIFF_FETCH_ERROR'; payload: number }
  | { type: 'OPEN_RESTORE_CONFIRM'; payload: number }
  | { type: 'CLOSE_RESTORE_CONFIRM' }
  | { type: 'RESTORE_START' }
  | { type: 'RESTORE_END' }
  | { type: 'RESET' };
